import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { UserItem } from '@/types/userItem';

export interface QueueCandidate {
  imageUrl: string;
  sourceUrl: string;
  score: number;
  condition?: string;
  price?: string;
}

export type QueueResult = 'applied' | 'skipped' | 'nomatch';

interface CacheEntry {
  status: 'idle' | 'loading' | 'ready' | 'error';
  candidates: QueueCandidate[];
  error?: string;
}

const PREFETCH_AHEAD = 2;

function itemToBody(item: UserItem, overrideTitle?: string) {
  return {
    title: overrideTitle ?? item.title,
    category: item.category,
    game: item.game || undefined,
    set_code: item.set_code || undefined,
    set_name: item.set_name || undefined,
    card_number: item.card_number || undefined,
    grading_company: item.grading_company || undefined,
    slab_grade: item.slab_grade || undefined,
    product_type: item.product_type || undefined,
    era: item.era || undefined,
    rarity: item.rarity || undefined,
    returnMultiple: true,
    offset: 0,
    excludeUrls: [],
  };
}

/**
 * Manages a rapid-fire review queue over photoless items.
 * - Prefetches candidates for the next PREFETCH_AHEAD items so tapping feels instant.
 * - Applies chosen stock image URL to `user_items.stock_image_url` with optimistic cache update.
 */
export function useStockImageQueue(items: UserItem[], open: boolean) {
  const queryClient = useQueryClient();
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState<Record<string, QueueResult>>({});
  const [cache, setCache] = useState<Record<string, CacheEntry>>({});
  const inFlight = useRef<Set<string>>(new Set());

  // Reset when opened with a new list
  useEffect(() => {
    if (open) {
      setIndex(0);
      setResults({});
      setCache({});
      inFlight.current = new Set();
    }
  }, [open, items.length]);

  const fetchFor = useCallback(
    async (item: UserItem, overrideTitle?: string) => {
      if (inFlight.current.has(item.id)) return;
      inFlight.current.add(item.id);
      setCache((c) => ({ ...c, [item.id]: { status: 'loading', candidates: c[item.id]?.candidates ?? [] } }));
      try {
        const { data, error } = await supabase.functions.invoke('fetch-stock-image', {
          body: itemToBody(item, overrideTitle),
        });
        if (error) throw error;
        const candidates: QueueCandidate[] = data?.options ?? [];
        setCache((c) => ({ ...c, [item.id]: { status: 'ready', candidates } }));
      } catch (err: any) {
        setCache((c) => ({
          ...c,
          [item.id]: { status: 'error', candidates: [], error: err?.message ?? 'Failed to load' },
        }));
      } finally {
        inFlight.current.delete(item.id);
      }
    },
    [],
  );

  // Kick off fetches for current + next N
  useEffect(() => {
    if (!open) return;
    for (let i = index; i < Math.min(items.length, index + 1 + PREFETCH_AHEAD); i++) {
      const it = items[i];
      if (!it) continue;
      const entry = cache[it.id];
      if (!entry || entry.status === 'idle') {
        fetchFor(it);
      }
    }
  }, [open, index, items, cache, fetchFor]);

  const advance = useCallback(() => {
    setIndex((i) => Math.min(items.length, i + 1));
  }, [items.length]);

  const apply = useCallback(
    async (imageUrl: string) => {
      const current = items[index];
      if (!current) return;
      const itemId = current.id;

      // Optimistic cache update
      queryClient.setQueryData<UserItem[] | undefined>(['my-items'], (prev) => {
        if (!prev) return prev;
        return prev.map((it) => (it.id === itemId ? { ...it, stock_image_url: imageUrl } : it));
      });
      setResults((r) => ({ ...r, [itemId]: 'applied' }));
      advance();

      const { error } = await supabase
        .from('user_items')
        .update({ stock_image_url: imageUrl })
        .eq('id', itemId);
      if (error) {
        console.error('Apply stock image failed:', error);
        // Roll back cache flag; user can retry via inventory
        setResults((r) => ({ ...r, [itemId]: 'skipped' }));
      }
    },
    [index, items, queryClient, advance],
  );

  const skip = useCallback(() => {
    const current = items[index];
    if (current) setResults((r) => ({ ...r, [current.id]: 'skipped' }));
    advance();
  }, [index, items, advance]);

  const noMatch = useCallback(() => {
    const current = items[index];
    if (current) setResults((r) => ({ ...r, [current.id]: 'nomatch' }));
    advance();
  }, [index, items, advance]);

  const refetchCurrent = useCallback(
    (overrideTitle?: string) => {
      const current = items[index];
      if (!current) return;
      setCache((c) => ({ ...c, [current.id]: { status: 'idle', candidates: [] } }));
      fetchFor(current, overrideTitle);
    },
    [index, items, fetchFor],
  );

  const finish = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['my-items'] });
  }, [queryClient]);

  const current = items[index];
  const currentEntry = current ? cache[current.id] : undefined;
  const summary = {
    applied: Object.values(results).filter((r) => r === 'applied').length,
    skipped: Object.values(results).filter((r) => r === 'skipped').length,
    nomatch: Object.values(results).filter((r) => r === 'nomatch').length,
  };

  return {
    index,
    total: items.length,
    current,
    currentEntry,
    isDone: index >= items.length,
    summary,
    apply,
    skip,
    noMatch,
    refetchCurrent,
    finish,
  };
}
